import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { UserAttachmentEditionCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { FileCRUDType } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type UserAttachmentEditionCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  fileOnRestoreFile: (id: string) => void;
  fileOnLoadFile: (file: any) => void;
  fileOnCommitFile: (changes: GridChangeSet) => void;
  fileFiles: FileCRUDType[];
  fileError?: string;
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notes: string;
  notesError?: string;
  dummyCRUDRestoredObservable: number;
  userAttachmentEditionCreateStore: UserAttachmentEditionCreateStore;
};

export const UserAttachmentEditionCreateForm: React.FC<UserAttachmentEditionCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      fileOnRestoreFile,
      fileOnLoadFile,
      fileOnCommitFile,
      fileFiles,
      fileError,
      notes = "",
      notesError,
      onNotesChange = () => {},
      dummyCRUDRestoredObservable,
      userAttachmentEditionCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="UserAttachmentEdition"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "notes":
                    onNotesChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={userAttachmentEditionCreateStore.getCardErrors("null")}
              title={langStore.groupUserAttachmentEditionLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(userAttachmentEditionCreateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    userAttachmentEditionCreateStore.warnings["name"]?.message
                  }
                  label={langStore.UserAttachmentEditionNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.UserAttachmentEditionNameDescription}
                />
                <LoadFilesCRUDCompact
                  rows={fileFiles}
                  maxRows={1}
                  acceptFileTypes=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.gif"
                  showAddCommand={true}
                  showDeleteCommand={true}
                  label={langStore.UserAttachmentEditionFileLabel}
                  description={langStore.UserAttachmentEditionFileDescription}
                  showLabel={true}
                  onLoadFile={(file: File) => fileOnLoadFile(file)}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                  onRestore={(id: string) => fileOnRestoreFile(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    fileOnCommitFile(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={fileError}
                />

                <InputText
                  value={notes}
                  error={Boolean(notesError)}
                  warning={
                    !notesError &&
                    Boolean(userAttachmentEditionCreateStore.warnings["notes"])
                  }
                  helperText={
                    notesError ||
                    userAttachmentEditionCreateStore.warnings["notes"]?.message
                  }
                  label={langStore.UserAttachmentEditionNotesLabel}
                  onChange={(value: string) => onNotesChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.UserAttachmentEditionNotesDescription}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
